import { useEffect, useRef, useState } from 'react';
import { engine } from './engine/engine';
import { InputSystem } from './engine/input';
import { CLASSIC_ZERO } from './engine/tables/classicZero';
import { BUILT_IN_LEVELS } from './engine/tables/builtInLevels';
import type { TableDefinition } from './engine/tables/definition';
import { currentPlayerPreferences, useSettings, useSession, type PlayerPreferences } from './ui/store';
import { PHYSICS_DT } from './engine/types';
import DebugCanvas from './render2d/DebugCanvas';
import Table3DShowroom from './render3d/Table3DShowroom';
import { cameraModeLabel, nextCameraMode } from './render3d/cameraModes';
import CreatorStudio from './editor/CreatorStudio';
import { createEmptyTable } from './editor/emptyTable';
import { isPlayableTableDefinition, validateTableDefinition } from './editor/tableValidation';
import { clearSavedTable, loadSavedTable, saveTable } from './editor/tableStorage';
import TouchControls, { type TouchControlState } from './ui/TouchControls';
import ControlGuide from './ui/ControlGuide';
import HomeHero from './ui/HomeHero';
import SettingsPanel from './ui/SettingsPanel';
import GameOverPanel from './ui/GameOverPanel';
import loadingStyles from './ui/TableLoadingScreen.module.css';
import { nextAnimationFrame, waitForRendererCanvas } from './ui/renderReady';
import CommunityPanel from './community/CommunityPanel';
import { trackingTarget } from './community/trackingTarget';
import { playGameEventAudio } from './assets/gameAudio';
import { pinballAudio } from './assets/proceduralAudio';
import {
  createCommunityTable,
  finishTrackedPlay,
  getCommunityTable,
  getPlayerPreferences,
  savePlayerPreferences,
  startTrackedPlay,
  updateCommunityTable,
  type CommunityPlayer,
  type CommunityTableDetail,
} from './community/api';
import {
  buildEditPath,
  buildHomePath,
  buildPlayPath,
  parseAppRoute,
  routeTableId,
} from './navigation/appRoutes';
import {
  loadLocalScores,
  normalizeInitials,
  saveLocalScore,
  type LocalScore,
} from './ui/localLeaderboard';
import styles from './App.module.css';

function tableIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return routeTableId(window.location.pathname, window.location.search);
}

function updateBrowserPath(path: string, replace = false): void {
  if (typeof window === 'undefined') return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === path) return;
  if (replace) window.history.replaceState({}, '', path);
  else window.history.pushState({}, '', path);
}

function updateTableUrl(tableId: string, replace = false): void {
  if (!tableId) return;
  updateBrowserPath(buildHomePath(tableId), replace);
}

function builtInTable(tableId: string | null): TableDefinition | null {
  if (!tableId) return null;
  return BUILT_IN_LEVELS.find((level) => level.table.id === tableId)?.table ?? null;
}

function initialTable(): TableDefinition {
  const requestedId = tableIdFromUrl();
  const requestedBuiltIn = builtInTable(requestedId);
  if (requestedBuiltIn) return requestedBuiltIn;

  const saved = loadSavedTable();
  if (saved && saved.id !== CLASSIC_ZERO.id && !saved.provenance?.isOfficial && (!requestedId || saved.id === requestedId)) return saved;
  const selectedId = useSettings.getState().selectedTableId;
  return BUILT_IN_LEVELS.find((level) => level.table.id === selectedId)?.table ?? BUILT_IN_LEVELS[0]!.table;
}

export default function App() {
  const renderMode = useSettings((state) => state.renderMode);
  const setRenderMode = useSettings((state) => state.setRenderMode);
  const cameraMode = useSettings((state) => state.cameraMode);
  const setCameraMode = useSettings((state) => state.setCameraMode);
  const keymap = useSettings((state) => state.keymap);
  const masterVolume = useSettings((state) => state.masterVolume);
  const muted = useSettings((state) => state.muted);
  const toggleMute = useSettings((state) => state.toggleMute);
  const effectsEnabled = useSettings((state) => state.effectsEnabled);
  const attractMotion = useSettings((state) => state.attractMotion);
  const selectedTableId = useSettings((state) => state.selectedTableId);
  const setSelectedTableId = useSettings((state) => state.setSelectedTableId);

  const score = useSession((state) => state.score);
  const multiplier = useSession((state) => state.multiplier);
  const nudgesRemaining = useSession((state) => state.nudgesRemaining);
  const ballsRemaining = useSession((state) => state.ballsRemaining);
  const ballSaver = useSession((state) => state.ballSaver);
  const tilted = useSession((state) => state.tilted);
  const setSession = useSession((state) => state.setSession);

  const [inMenu, setInMenu] = useState(true);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [initials, setInitials] = useState('');
  const [scoreSaved, setScoreSaved] = useState(false);
  const [localScores, setLocalScores] = useState<LocalScore[]>(loadLocalScores);
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0);
  const [currentTable, setCurrentTable] = useState<TableDefinition>(initialTable);
  const [remoteTable, setRemoteTable] = useState<CommunityTableDetail | null>(null);
  const [player, setPlayer] = useState<CommunityPlayer | null>(null);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editorPlaytest, setEditorPlaytest] = useState(false);
  const [menuNotice, setMenuNotice] = useState('');
  const [pendingRoutePlay, setPendingRoutePlay] = useState<string | null>(null);

  const touchRef = useRef<TouchControlState>({ left: false, right: false, plunger: false, nudge: false });
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackedPlayRef = useRef<Promise<string | null> | null>(null);
  const playStartedAtRef = useRef(0);
  const gameOverHandledRef = useRef(false);
  const editorPlaytestRef = useRef(false);
  const editorPreviewPendingRef = useRef(false);

  const playable = isPlayableTableDefinition(currentTable);
  const playabilityErrors = playable ? [] : validateTableDefinition(currentTable);

  const releaseHeldControls = (): void => {
    touchRef.current.left = false;
    touchRef.current.right = false;
    touchRef.current.plunger = false;
    touchRef.current.nudge = false;
    engine.setFlipper('left', false);
    engine.setFlipper('right', false);
  };

  const clearPlaytestMode = (): void => {
    editorPreviewPendingRef.current = false;
    editorPlaytestRef.current = false;
    setEditorPlaytest(false);
  };

  const finishPendingPlay = (
    pending: Promise<string | null> | null,
    details: { durationMs: number; score: number; ballsUsed: number },
  ): Promise<void> => {
    if (!pending) return Promise.resolve();
    return pending.then((playId) => finishTrackedPlay(playId, details));
  };

  const beginTrackedPlay = (): Promise<string | null> | null => {
    const target = trackingTarget(currentTable, remoteTable);
    return target ? startTrackedPlay(target.tableId, target.versionId) : null;
  };

  useEffect(() => {
    pinballAudio.setVolume(masterVolume);
    pinballAudio.setMuted(muted);
  }, [masterVolume, muted]);

  useEffect(() => {
    if (!player || !preferencesReady) return;
    const timeout = window.setTimeout(() => {
      void savePlayerPreferences(currentPlayerPreferences()).catch(() => {
        // Local persistence remains authoritative when account sync is offline.
      });
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [
    player,
    preferencesReady,
    renderMode,
    cameraMode,
    keymap,
    masterVolume,
    muted,
    effectsEnabled,
    attractMotion,
    selectedTableId,
  ]);

  useEffect(() => {
    let alive = true;
    gameOverHandledRef.current = false;
    const unsubscribe = engine.onSnapshot((snapshot) => {
      if (!alive) return;
      setSession({
        score: snapshot.score,
        multiplier: snapshot.multiplier,
        nudgesRemaining: snapshot.nudgesRemaining,
        ballsRemaining: snapshot.ballsRemaining,
        ballSaver: snapshot.ballSaver,
        tilted: snapshot.tilted,
      });
      setPaused(snapshot.phase === 'paused');
      if (snapshot.phase === 'game-over' && !gameOverHandledRef.current) {
        gameOverHandledRef.current = true;
        const trackedPlay = trackedPlayRef.current;
        trackedPlayRef.current = null;
        if (!editorPlaytestRef.current) {
          const finished = finishPendingPlay(trackedPlay, {
            durationMs: playStartedAtRef.current ? Math.max(0, performance.now() - playStartedAtRef.current) : 0,
            score: snapshot.score,
            ballsUsed: currentTable.board.ballsPerGame,
          });
          void finished.finally(() => setLeaderboardRefreshKey((value) => value + 1));
        }
        releaseHeldControls();
        setGameOver(true);
        setInitials('');
        setScoreSaved(false);
        setPaused(false);
      }
    });

    if (!isPlayableTableDefinition(currentTable)) {
      engine.stop();
      editorPreviewPendingRef.current = false;
      setLoading(false);
      return () => {
        alive = false;
        unsubscribe();
      };
    }

    if (!editorPreviewPendingRef.current) {
      engine.stop();
      setLoading(false);
      return () => {
        alive = false;
        unsubscribe();
      };
    }

    setLoading(true);
    engine.loadTable(currentTable)
      .then(() => {
        if (!alive) return;
        editorPreviewPendingRef.current = false;
        releaseHeldControls();
        gameOverHandledRef.current = false;
        setGameOver(false);
        setScoreSaved(false);
        setInitials('');
        setPaused(false);
        trackedPlayRef.current = null;
        setInMenu(false);
        void (async () => {
          await nextAnimationFrame();
          if (useSettings.getState().renderMode === '3d') await waitForRendererCanvas(viewportRef.current);
          if (!alive) return;
          engine.start();
          playStartedAtRef.current = performance.now();
          setLoading(false);
        })();
      })
      .catch((error) => {
        console.error('Physics init failed', error);
        editorPreviewPendingRef.current = false;
        if (alive) {
          setLoading(false);
          if (editorPlaytestRef.current) {
            setEditing(true);
            setInMenu(true);
            clearPlaytestMode();
            setMenuNotice('The playtest could not start. Check the layout and try again.');
          }
        }
      });

    return () => {
      alive = false;
      unsubscribe();
    };
  }, [currentTable, setSession]);

  useEffect(() => engine.onEvent((event) => playGameEventAudio(event, currentTable)), [currentTable]);

  useEffect(() => {
    if (inMenu || loading || gameOver) return;
    const input = new InputSystem(keymap);
    let pendingNudgeFrame: number | null = null;
    const queueDirectionalNudge = (): void => {
      if (pendingNudgeFrame !== null) return;
      pendingNudgeFrame = requestAnimationFrame(() => {
        pendingNudgeFrame = null;
        const x = (input.isPressed('nudge-right') ? 1 : 0) - (input.isPressed('nudge-left') ? 1 : 0);
        const y = input.isPressed('nudge-up') ? -1 : 0;
        if (x !== 0 || y !== 0) engine.nudge({ x, y });
      });
    };
    const offEdge = input.onAction((action) => {
      if (action === 'toggle-view') setRenderMode(renderMode === '3d' ? '2d' : '3d');
      if (action === 'mute') {
        pinballAudio.unlock();
        toggleMute();
      }
      if (action === 'pause') engine.togglePause();
      if (action === 'nudge-left' || action === 'nudge-right' || action === 'nudge-up') queueDirectionalNudge();
    });
    input.start();

    let animationFrame = 0;
    let plungerWasPressed = false;
    const drive = () => {
      if (engine.currentPhase === 'paused') {
        engine.setFlipper('left', false);
        engine.setFlipper('right', false);
        plungerWasPressed = false;
        animationFrame = requestAnimationFrame(drive);
        return;
      }
      engine.setFlipper('left', input.isPressed('flipper-left') || touchRef.current.left);
      engine.setFlipper('right', input.isPressed('flipper-right') || touchRef.current.right);
      const plungerPressed = input.isPressed('plunger') || touchRef.current.plunger;
      if (plungerPressed) engine.chargePlunger(PHYSICS_DT * 4);
      else if (plungerWasPressed) engine.releasePlunger();
      plungerWasPressed = plungerPressed;
      animationFrame = requestAnimationFrame(drive);
    };
    animationFrame = requestAnimationFrame(drive);
    return () => {
      cancelAnimationFrame(animationFrame);
      if (pendingNudgeFrame !== null) cancelAnimationFrame(pendingNudgeFrame);
      offEdge();
      input.stop();
      releaseHeldControls();
    };
  }, [inMenu, loading, gameOver, keymap, renderMode, setRenderMode, toggleMute]);

  const handlePlayerChange = async (next: CommunityPlayer | null) => {
    setPreferencesReady(false);
    if (!next) {
      setPlayer(null);
      return;
    }
    try {
      const cloud = await getPlayerPreferences();
      if (cloud) {
        useSettings.getState().applyPreferences(cloud as PlayerPreferences);
        const cloudTable = cloud.selectedTableId
          ? BUILT_IN_LEVELS.find((level) => level.table.id === cloud.selectedTableId)?.table
          : null;
        if (cloudTable && currentTable.provenance?.isOfficial) {
          clearPlaytestMode();
          setCurrentTable(cloudTable);
          setRemoteTable(null);
          updateTableUrl(cloudTable.id);
        }
      } else {
        await savePlayerPreferences(currentPlayerPreferences());
      }
    } catch {
      // Settings are still safely persisted locally if account sync is offline.
    } finally {
      setPlayer(next);
      setPreferencesReady(true);
    }
  };

  const startGame = async () => {
    if (!isPlayableTableDefinition(currentTable)) {
      setMenuNotice(`Finish the machine before playing: ${validateTableDefinition(currentTable).join(' ')}`);
      return;
    }
    clearPlaytestMode();
    const tableId = remoteTable?.id ?? currentTable.id;
    updateBrowserPath(buildPlayPath(tableId));
    gameOverHandledRef.current = false;
    setGameOver(false);
    setScoreSaved(false);
    setPaused(false);
    setLoading(true);
    setMenuNotice('');
    releaseHeldControls();
    pinballAudio.unlock();
    setInMenu(false);

    await nextAnimationFrame();

    try {
      trackedPlayRef.current = beginTrackedPlay();
      if (engine.table !== currentTable || !engine.restart()) await engine.loadTable(currentTable);
      if (renderMode === '3d') await waitForRendererCanvas(viewportRef.current);
      releaseHeldControls();
      engine.start();
      playStartedAtRef.current = performance.now();
    } catch (error) {
      console.error('Game start failed', error);
      engine.stop();
      setInMenu(true);
      updateTableUrl(tableId, true);
      setMenuNotice('The table could not start. Choose another level or reset to Grand Circuit.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pendingRoutePlay || currentTable.id !== pendingRoutePlay || editing) return;
    setPendingRoutePlay(null);
    void startGame();
  }, [pendingRoutePlay, currentTable, editing]);

  useEffect(() => {
    let cancelled = false;

    const restoreRoute = async (): Promise<void> => {
      const route = parseAppRoute(window.location.pathname, window.location.search);
      clearPlaytestMode();
      setPendingRoutePlay(null);

      if (route.kind === 'create') {
        const table = createEmptyTable();
        saveTable(table);
        setCurrentTable(table);
        setRemoteTable(null);
        setGameOver(false);
        setInMenu(true);
        setLoading(false);
        setEditing(true);
        setMenuNotice('New local draft created. You can publish it publicly without signing in.');
        updateBrowserPath(buildEditPath(table.id), true);
        return;
      }

      const requestedId = route.tableId;
      if (!requestedId) {
        engine.stop();
        setEditing(false);
        setInMenu(true);
        setGameOver(false);
        setLoading(false);
        setMenuNotice('');
        return;
      }

      let resolvedTable: TableDefinition | null = builtInTable(requestedId);
      let resolvedRemote: CommunityTableDetail | null = null;
      let notice = '';

      if (resolvedTable) {
        useSettings.getState().setSelectedTableId(resolvedTable.id);
      } else {
        const saved = loadSavedTable();
        if (saved?.id === requestedId) {
          resolvedTable = saved;
        } else {
          setLoading(true);
          try {
            resolvedRemote = await getCommunityTable(requestedId, route.kind === 'edit');
            resolvedTable = resolvedRemote.definition;
            notice = route.kind === 'edit'
              ? `Editing ${resolvedRemote.name}.`
              : `Loaded ${resolvedRemote.name} from its shared link.`;
          } catch {
            if (route.kind === 'edit') {
              try {
                resolvedRemote = await getCommunityTable(requestedId);
                resolvedTable = resolvedRemote.definition;
                notice = `You can view ${resolvedRemote.name}, but only its owner can edit the published machine.`;
              } catch {
                // Fall through to the shared unavailable message below.
              }
            }
            if (!resolvedTable && !cancelled) {
              setLoading(false);
              setEditing(false);
              setInMenu(true);
              setMenuNotice('That shared table is unavailable or private. You can still choose another machine below.');
              return;
            }
          }
        }
      }

      if (cancelled || !resolvedTable) return;
      setCurrentTable(resolvedTable);
      setRemoteTable(resolvedRemote);
      setGameOver(false);
      setPaused(false);
      setInMenu(true);
      setLoading(false);
      setEditing(route.kind === 'edit');
      setMenuNotice(notice);
      if (route.kind === 'play') setPendingRoutePlay(resolvedTable.id);
    };

    void restoreRoute();
    const onPopState = () => void restoreRoute();
    window.addEventListener('popstate', onPopState);
    return () => {
      cancelled = true;
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const startEditorPlaytest = (table: TableDefinition): void => {
    const errors = validateTableDefinition(table);
    if (errors.length > 0) {
      setMenuNotice(`Finish the machine before playtesting: ${errors.slice(0, 3).join(' ')}`);
      return;
    }
    releaseHeldControls();
    pinballAudio.unlock();
    trackedPlayRef.current = null;
    editorPlaytestRef.current = true;
    editorPreviewPendingRef.current = true;
    setEditorPlaytest(true);
    setGameOver(false);
    setScoreSaved(false);
    setPaused(false);
    setMenuNotice('Loading the exact unsaved layout for playtest…');
    setInMenu(true);
    setEditing(false);
    setCurrentTable({ ...table });
  };

  const resetGame = async (): Promise<void> => {
    releaseHeldControls();
    pinballAudio.unlock();

    const previousTrackedPlay = trackedPlayRef.current;
    trackedPlayRef.current = null;
    if (previousTrackedPlay && !editorPlaytestRef.current) {
      void finishPendingPlay(previousTrackedPlay, {
        durationMs: playStartedAtRef.current ? Math.max(0, performance.now() - playStartedAtRef.current) : 0,
        score,
        ballsUsed: Math.max(0, currentTable.board.ballsPerGame - ballsRemaining),
      });
    }

    if (!engine.restart()) return;
    gameOverHandledRef.current = false;
    setGameOver(false);
    setScoreSaved(false);
    setInitials('');
    setPaused(false);
    releaseHeldControls();
    engine.start();
    playStartedAtRef.current = performance.now();
    trackedPlayRef.current = editorPlaytestRef.current ? null : beginTrackedPlay();
  };

  const backToHomepage = (): void => {
    releaseHeldControls();
    engine.stop();
    gameOverHandledRef.current = false;
    setGameOver(false);
    setPaused(false);
    clearPlaytestMode();
    setPendingRoutePlay(null);
    setInMenu(true);
    setEditing(false);
    updateBrowserPath(buildHomePath(remoteTable?.id ?? currentTable.id));
  };

  const backToEditor = (): void => {
    releaseHeldControls();
    engine.stop();
    trackedPlayRef.current = null;
    gameOverHandledRef.current = false;
    setGameOver(false);
    setPaused(false);
    clearPlaytestMode();
    setInMenu(true);
    setEditing(true);
    updateBrowserPath(buildEditPath(remoteTable?.id ?? currentTable.id), true);
    setMenuNotice('Playtest ended. Your unsaved layout is still here.');
  };

  const handleEditorSave = async (table: TableDefinition) => {
    clearPlaytestMode();
    setMenuNotice('');
    if (remoteTable?.canEdit && remoteTable.id === currentTable.id) {
      try {
        const updated = await updateCommunityTable(remoteTable.id, { ...table, id: remoteTable.id });
        setCurrentTable(updated.definition);
        setRemoteTable({ ...remoteTable, ...updated, canEdit: true });
        updateTableUrl(updated.id, true);
        setEditing(false);
        setMenuNotice('Cloud draft saved. Publish it from My Tables when it is ready.');
        return;
      } catch (error) {
        setMenuNotice(error instanceof Error ? error.message : 'Cloud draft could not be saved.');
        return;
      }
    }

    try {
      const created = await createCommunityTable(table);
      setCurrentTable(created.definition);
      setRemoteTable(created);
      updateTableUrl(created.id, true);
      setEditing(false);
      setMenuNotice(
        player
          ? 'Saved to My Tables as a draft. Publish it when it is ready.'
          : 'Saved to this browser’s guest workshop. You can publish it publicly and share it without signing in.',
      );
      return;
    } catch (error) {
      console.warn('Community draft save failed; preserving locally.', error);
    }

    saveTable(table);
    setCurrentTable(table);
    setRemoteTable(null);
    updateTableUrl(table.id, true);
    setEditing(false);
    setMenuNotice('Community save is unavailable, so this draft was kept safely on this device.');
  };

  const selectBuiltIn = (table: TableDefinition) => {
    clearPlaytestMode();
    clearSavedTable();
    setSelectedTableId(table.id);
    setCurrentTable(table);
    setRemoteTable(null);
    updateTableUrl(table.id);
    setEditing(false);
    setGameOver(false);
    setMenuNotice('');
  };

  const newEmptyTable = () => {
    clearPlaytestMode();
    const table = createEmptyTable();
    saveTable(table);
    setCurrentTable(table);
    setRemoteTable(null);
    setGameOver(false);
    setMenuNotice('Starter machine ready. Shape the hardware, add shots, then preview it.');
    updateBrowserPath(buildEditPath(table.id));
    setEditing(true);
  };

  const playCommunityTable = (table: CommunityTableDetail) => {
    clearPlaytestMode();
    updateBrowserPath(buildHomePath(table.id), true);
    updateBrowserPath(buildPlayPath(table.id));
    setCurrentTable(table.definition);
    setRemoteTable(table);
    setEditing(false);
    setGameOver(false);
    setMenuNotice(`Loading ${table.name}…`);
    setPendingRoutePlay(table.definition.id);
  };

  const editCommunityTable = (table: CommunityTableDetail) => {
    clearPlaytestMode();
    setCurrentTable(table.definition);
    setRemoteTable(table);
    updateBrowserPath(buildEditPath(table.id));
    setEditing(true);
    setInMenu(true);
    setMenuNotice('');
  };

  const editCurrentTable = () => {
    clearPlaytestMode();
    updateBrowserPath(buildEditPath(remoteTable?.id ?? currentTable.id));
    setEditing(true);
    setInMenu(true);
  };

  const cancelEditor = () => {
    clearPlaytestMode();
    setEditing(false);
    setInMenu(true);
    updateBrowserPath(buildHomePath(remoteTable?.id ?? currentTable.id), true);
  };

  const submitScore = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editorPlaytestRef.current) return;
    const normalized = normalizeInitials(initials);
    if (normalized.length !== 3) return;
    setLocalScores(saveLocalScore({ initials: normalized, score, tableId: currentTable.id }));
    setScoreSaved(true);
  };

  return (
    <div className={styles.app}>
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} signedIn={Boolean(player)} />}
      {editing ? (
        <CreatorStudio
          initialTable={currentTable}
          onSave={(table) => void handleEditorSave(table)}
          onPreview={startEditorPlaytest}
          onCancel={cancelEditor}
        />
      ) : inMenu ? (
        <main className={styles.menu}>
          <HomeHero
            table={currentTable}
            loading={loading}
            playable={playable}
            renderMode={renderMode}
            effectsEnabled={effectsEnabled}
            attractMotion={attractMotion}
            onSelect={selectBuiltIn}
            onPlay={() => void startGame()}
            onRemix={editCurrentTable}
            onNewTable={newEmptyTable}
          />

          {menuNotice && <p className={styles.notice}>{menuNotice}</p>}
          {!playable && !editing && <p className={styles.notice}>Draft not playable yet · {playabilityErrors.slice(0, 3).join(' ')}</p>}

          <CommunityPanel onPlay={playCommunityTable} onEdit={editCommunityTable} onPlayerChange={(next) => void handlePlayerChange(next)} />
        </main>
      ) : (
        <div className={styles.game}>
          <header className={styles.hud}>
            <div className={styles.hudItem}>SCORE <strong>{score.toLocaleString()}</strong></div>
            <div className={styles.hudItem}>BALLS <strong>{'●'.repeat(Math.max(0, ballsRemaining)) || '—'}</strong></div>
            <div className={styles.hudItem}>MULTI <strong>{multiplier}X</strong></div>
            <div className={styles.hudItem}>NUDGE <strong>{nudgesRemaining}</strong></div>
            {editorPlaytest && <span className={styles.hudStatus} role="status">PLAYTEST</span>}
            {ballSaver && <span className={`${styles.hudStatus} ${styles.ballSaver}`} role="status">BALL SAVE</span>}
            {tilted && <span className={`${styles.hudStatus} ${styles.tilted}`} role="status">TILT</span>}
            {paused && <span className={styles.hudStatus} role="status">PAUSED</span>}
            {!loading && (
              <div className={styles.hudActions}>
                {editorPlaytest && <button className="secondary" onClick={backToEditor} title="Return to the unsaved layout">BACK TO EDITOR</button>}
                <button className="secondary" onClick={() => engine.togglePause()} title={paused ? 'Resume (P)' : 'Pause (P)'}>{paused ? '▶' : 'Ⅱ'}</button>
                <button className="secondary" onClick={() => void resetGame()} title="Reset this game" aria-label="Reset this game">RESET</button>
                <button className="secondary" onClick={() => { pinballAudio.unlock(); toggleMute(); }} title={muted ? 'Unmute sound' : 'Mute sound'}>{muted ? '🔇' : '🔊'}</button>
                <button className="secondary" onClick={() => setSettingsOpen(true)} title="Settings">⚙</button>
                {renderMode === '3d' && <button className="secondary" onClick={() => setCameraMode(nextCameraMode(cameraMode))} title="Change 3D camera">📷 {cameraModeLabel(cameraMode)}</button>}
                <button className="secondary" onClick={() => setRenderMode(renderMode === '3d' ? '2d' : '3d')} title="Toggle view (V)">{renderMode === '3d' ? '3D' : '2D'}</button>
                {!editorPlaytest && <button className="secondary" onClick={backToHomepage} title="Return to the table browser">CLOSE</button>}
              </div>
            )}
          </header>
          <div ref={viewportRef} className={styles.viewport} data-mode={renderMode} aria-busy={loading}>
            {renderMode === '3d' ? <Table3DShowroom table={currentTable} /> : <DebugCanvas table={currentTable} />}
            {loading && (
              <div className={loadingStyles.screen} role="status" aria-live="polite" aria-label={`Loading ${currentTable.name}`}>
                <div className={loadingStyles.card}>
                  <img src="/brand/logo-square.webp" alt="" aria-hidden="true" />
                  <div className={loadingStyles.spinner} aria-hidden="true" />
                  <strong>{renderMode === '3d' ? `PREPARING 3D ${currentTable.name.toUpperCase()}` : `LOADING ${currentTable.name.toUpperCase()}`}</strong>
                  <div className={loadingStyles.progressRow}>
                    <progress className={loadingStyles.progress} max={100} aria-label="Preparing game engine" />
                    <span className={loadingStyles.percent}>…</span>
                  </div>
                  <span>Setting up the table…</span>
                </div>
              </div>
            )}
            {!loading && !gameOver && <ControlGuide />}
            {!loading && !gameOver && <TouchControls state={touchRef} nudgesRemaining={nudgesRemaining} onNudge={() => engine.nudge({ x: 0, y: -1 })} />}
          </div>
          {gameOver && (
            <GameOverPanel
              score={score}
              tableId={currentTable.id}
              tableName={currentTable.name}
              communityTableId={editorPlaytest ? undefined : remoteTable?.id}
              leaderboardRefreshKey={leaderboardRefreshKey}
              initials={initials}
              scoreSaved={scoreSaved}
              localScores={localScores}
              scoreEntryEnabled={!editorPlaytest}
              onInitialsChange={(value) => setInitials(normalizeInitials(value))}
              onSubmitScore={submitScore}
              onTryAgain={() => void resetGame()}
              onHome={editorPlaytest ? backToEditor : backToHomepage}
              homeLabel={editorPlaytest ? 'Back to Editor' : 'Back to Homepage'}
            />
          )}
        </div>
      )}
    </div>
  );
}
